<template>
  <div class="editor-price-details-list flex flex-col gap-200">
    <div
      v-if="isSubscription"
      class="bg-accent-info-info-extralight flex flex-col gap-025 py-025 px-100 rounded-s"
      data-testid="price-details-subscription"
    >
      <div class="flex justify-between items-center">
        <p class="paragraph-bold">{{ $t('checkout.subscription.title') }}</p>
        <LcIcon name="subscription" size="small" />
      </div>

      <p class="paragraph-small-text text-secondary-secondary">
        {{ $t('checkout.subscription.disclaimer') }}
      </p>
    </div>

    <LcDescriptionList
      v-for="(group, index) in currentPrice.groupedPlays"
      :key="index"
      class="gap-none"
    >
      <EditorPriceDetailsPlay
        v-bind="{ lines: group.virtualPlays, name: group.name, numberOfPlays: group.plays }"
      >
        <template v-if="isNumberOrDateLottery">
          {{ group.name === EXTRA_HIT ? '' : $t(`lottery.name.${lotteryId}`) }}
          {{
            group.name === EXTRA_HIT
              ? $t(`lottery.${lotteryId}.${group.name}`)
              : $t('common.words.line')
          }}
        </template>
        <template v-else>
          {{ formatMoney(group.wagerAmount, { fractionDigits: 0 }) }}
          {{ getPerPlayTranslation(lotteryId, 'betType', String(group.betType)) }}
        </template>
      </EditorPriceDetailsPlay>

      <dd class="text-right">{{ formatMoney(group.price.sum) }}</dd>

      <template v-for="addOn of group.addOns" :key="addOn.name">
        <dt>
          {{ $t('tiertable.fielddivider') }}
          {{ $t(`lottery.addon.${addOn.name}`) }}
        </dt>
        <dd class="text-right">{{ formatMoney(addOn.price.sum) }}</dd>
      </template>
    </LcDescriptionList>

    <LcDescriptionList
      v-if="flaggedCount"
      class="gap-none items-center"
      data-testid="price-details-incomplete"
    >
      <EditorPriceDetailsPlay
        class="text-accent-danger-danger"
        data-testid="price-details-incomplete-label"
        :number-of-plays="flaggedCount"
      >
        {{ $t('editor.pricedetails.incompleteline') }}
      </EditorPriceDetailsPlay>
      <dd class="editor-price-details-list-unpriced text-right">
        {{ $t('editor.pricedetails.notpricedyet') }}
      </dd>
    </LcDescriptionList>

    <LcDescriptionList
      v-if="prepaidSubscriptionAmount"
      class="py-025 px-050 rounded-s bg-accent-success-success-light"
      data-testid="price-details-prepaid"
    >
      <dt>{{ $t('lottery.pricebreakdown.prepaiddraws') }}</dt>
      <dd class="flex justify-end font-medium">
        {{
          $t('lottery.pricebreakdown.prepaiddraws.amount', { amount: prepaidSubscriptionAmount })
        }}
      </dd>
    </LcDescriptionList>
  </div>
</template>

<script lang="ts" setup>
import type { PriceBreakdown } from '@lottocom/price-calculation'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { NUMBER_LOTTERY_PER_PLAY_ADD_ON_NAME } from '../constants/numberLottery'

const EXTRA_HIT = NUMBER_LOTTERY_PER_PLAY_ADD_ON_NAME.ExtraHit

type Props = {
  currentPrice: PriceBreakdown
  flaggedCount?: number
  isSubscription?: boolean
  lotteryId: LotteryId
  prepaidSubscriptionAmount?: Maybe<number>
}

const { lotteryId } = defineProps<Props>()

const { getPerPlayTranslation } = useTranslations()

const { isDateLottery, isNumberLottery } = useLotteryType(lotteryId)

const isNumberOrDateLottery = computed(() => isNumberLottery.value || isDateLottery.value)
</script>

<style lang="scss" scoped>
.editor-price-details-list-unpriced {
  white-space: nowrap;
}
</style>
